import { useEffect, useState } from 'react';
import { useFetcher } from 'react-router';
import { cn } from '@dinewise/utils';
import { PaymentsBottomSheet } from '~/components/payments-bottom-sheet/payments-bottom-sheet.tsx';
import type { VenueOption } from './resolve-selected-venue.ts';

export const VENUE_FILTER_FETCHER_KEY = 'payments-venue-filter';

type VenueSelectorProps = {
  venues: VenueOption[];
  selectedVenueId: string | null;
  selectedVenueName: string;
  variant?: 'default' | 'compact';
};

export const VenueSelector = ({
  venues,
  selectedVenueId,
  selectedVenueName,
  variant = 'default',
}: VenueSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [pendingVenueId, setPendingVenueId] = useState<string | null>(selectedVenueId);
  const fetcher = useFetcher({ key: VENUE_FILTER_FETCHER_KEY });
  const isVenueFilterLoading = fetcher.state !== 'idle';

  useEffect(() => {
    if (!isOpen) {
      setPendingVenueId(selectedVenueId);
    }
  }, [isOpen, selectedVenueId]);

  const submitVenueFilter = (venueId: string | null) => {
    const formData = new FormData();
    formData.set('subaction', 'venueFilter');

    if (venueId) {
      formData.set('venueId', venueId);
    } else {
      formData.set('clear', 'true');
    }

    void fetcher.submit(formData, { method: 'post' });
  };

  const handleClose = () => {
    setPendingVenueId(selectedVenueId);
    setIsOpen(false);
  };

  const handleApply = () => {
    if (pendingVenueId !== selectedVenueId) {
      submitVenueFilter(pendingVenueId);
    }

    setIsOpen(false);
  };

  const handleClear = () => {
    submitVenueFilter(null);
    setIsOpen(false);
  };

  const venueNameClassName =
    variant === 'compact'
      ? 'font-sans text-ande-gray-500 text-sm leading-[21px]'
      : 'font-serif text-2xl text-ande-black leading-8';
  const chevronClassName =
    variant === 'compact'
      ? 'text-ande-gray-300 text-xs leading-4'
      : 'text-ande-gray-300 text-sm leading-5';

  if (venues.length <= 1) {
    return (
      <div className="flex items-center gap-2">
        <span className={venueNameClassName}>{selectedVenueName}</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        className="flex items-center gap-1.5"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-busy={isVenueFilterLoading}
        aria-label={
          isVenueFilterLoading
            ? `Updating venue: ${selectedVenueName}`
            : `Selected venue: ${selectedVenueName}`
        }
        onClick={() => setIsOpen(true)}
        disabled={isVenueFilterLoading}
      >
        <span
          className={cn(venueNameClassName, isVenueFilterLoading && 'animate-venue-loading-fade')}
        >
          {selectedVenueName}
        </span>
        <span className={chevronClassName} aria-hidden="true">
          ▾
        </span>
      </button>

      <PaymentsBottomSheet
        isOpen={isOpen}
        title="Switch Venue"
        maxHeightClass="max-h-[70vh]"
        onClose={handleClose}
        onApply={handleApply}
        onClear={handleClear}
        isApplying={isVenueFilterLoading}
      >
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className={cn(
              'flex items-center justify-between rounded-xl px-4 py-3.5 text-left',
              pendingVenueId === null
                ? 'bg-ande-black text-white'
                : 'bg-ande-surface-50 text-ande-black',
            )}
            onClick={() => setPendingVenueId(null)}
          >
            <span className="font-medium text-base leading-6">All Venues</span>
            {pendingVenueId === null ? (
              <span aria-hidden="true" className="text-sm leading-5">
                ✓
              </span>
            ) : null}
          </button>

          {venues.map(venue => {
            const isSelected = venue.id === pendingVenueId;

            return (
              <button
                key={venue.id}
                type="button"
                className={cn(
                  'flex items-center justify-between rounded-xl px-4 py-3.5 text-left',
                  isSelected ? 'bg-ande-black text-white' : 'bg-ande-surface-50 text-ande-black',
                )}
                onClick={() => setPendingVenueId(venue.id)}
              >
                <span className="font-medium text-base leading-6">{venue.name}</span>
                {isSelected ? (
                  <span aria-hidden="true" className="text-sm leading-5">
                    ✓
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </PaymentsBottomSheet>
    </>
  );
};
